<template>
  <div class="duration-value">
    <div v-if="statistics" class="statistics-bar">
      <div class="stat-card">
        <span class="stat-label">Vizite medii / pacient</span>
        <span class="stat-value">{{ statistics.avgVisitsPerPatient }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Zile medii între vizite</span>
        <span class="stat-value">{{ statistics.avgDaysBetweenVisits }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Durată medie (zile)</span>
        <span class="stat-value">{{ statistics.avgDurationDays }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Pacienți recurenți</span>
        <span class="stat-value">{{ statistics.recurringPatients }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Pacienți high-value</span>
        <span class="stat-value">{{ statistics.highValuePatients }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Vizită unică</span>
        <span class="stat-value">{{ statistics.singleVisitPatients }}</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Valoare totală extrasă</span>
        <span class="stat-value">{{ Number(statistics.totalValueExtracted || 0).toLocaleString('ro-RO') }} RON</span>
      </div>
      <div class="stat-card">
        <span class="stat-label">Completitudine date</span>
        <span class="stat-value">{{ statistics.dataCompletenessRate }}%</span>
      </div>
    </div>
    <div class="filter-container">
      <form class="search-filters" @submit.prevent="onApplyFilters">
        <label>
          Dată de la
          <input v-model="localFilters.dateFrom" type="date">
        </label>
        <label>
          Dată până la
          <input v-model="localFilters.dateTo" type="date">
        </label>
        <label>
          Ultimele luni
          <select v-model.number="localFilters.lastMonths">
            <option :value="null">-</option>
            <option v-for="month in monthOptions" :key="'last-' + month" :value="month">{{ month }} luni</option>
          </select>
        </label>
        <button type="submit" class="search-btn">
          <img src="../assets/icons2/search-icon.svg" alt="search-icon">
        </button>
      </form>

      <div class="action-bar">
        <button class="download-btn" @click="$emit('download', localFilters)" :disabled="isDownloading || isLoading">
          <span v-if="!isDownloading">📥 Descarcă Excel</span>
          <span v-else class="btn-spinner"></span>
        </button>
      </div>
    </div>
    <div class="table-wrapper" :class="{ 'is-loading': isLoading }">
      <div v-if="isLoading" class="table-loader-overlay">
        <div class="spinner"></div>
        <p>Se încarcă datele...</p>
      </div>

      <table class="table">
        <thead>
        <tr>
          <th>#</th>
          <th>Nume</th>
          <th>Prenume</th>
          <th>Oraș</th>
          <th>Medic</th>
          <th>Prima Vizită</th>
          <th>Ultima Vizită</th>
          <th>Durata Zile</th>
          <th>Durata Luni</th>
          <th>Total Vizite</th>
          <th>Vizite / An</th>
          <th>Medie Zile între Vizite</th>
          <th>Valoare Totală</th>
          <th>Valoare Plătită</th>
        </tr>
        </thead>
        <tbody>
        <tr v-if="items.length === 0">
          <td colspan="14" class="no-data">Nu există date disponibile</td>
        </tr>
        <tr v-else v-for="(item, index) in items" :key="item._id">
          <td>{{ index + 1 }}</td>
          <td>{{ item.nume }}</td>
          <td>{{ item.prenume }}</td>
          <td>{{ item.oras }}</td>
          <td>{{ item.medic }}</td>
          <td>{{ item.firstVisit }}</td>
          <td>{{ item.lastVisit }}</td>
          <td>{{ item.durationDays }}</td>
          <td>{{ item.durationMonths }}</td>
          <td>{{ item.totalVisits }}</td>
          <td>{{ item.visitsPerYear }}</td>
          <td>{{ item.avgDaysBetweenVisits }}</td>
          <td>{{ item.totalValue || '-' }}</td>
          <td>{{ item.paidValue || '-' }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div class="summary">
      <span>Total pacienți: <b>{{ total }}</b></span>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DurationValue',

  props: {
    items: { type: Array, default: () => [] },
    total: { type: Number, default: 0 },
    statistics: { type: Object, default: null },
    thresholdMonths: { type: Number, default: 0 },
    isLoading: { type: Boolean, default: false },
    isDownloading: { type: Boolean, default: false }
  },

  emits: ['apply-filters', 'download'],

  data () {
    return {
      monthOptions: [3, 6, 12],
      localFilters: {
        dateFrom: '',
        dateTo: '',
        lastMonths: null,
      }
    }
  },

  watch: {
    'localFilters.lastMonths' (val) {
      if (val != null) {
        this.localFilters.dateFrom = ''
        this.localFilters.dateTo = ''
      }
    }
  },

  methods: {
    onApplyFilters () {
      this.$emit('apply-filters', { ...this.localFilters })
    },
  }
}
</script>

<style lang="less">
@import '../assets/styles/var.less';

.duration-value {
  @media only screen and (min-width: 0) {
    .statistics-bar {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      margin-bottom: 50px;

      .stat-card {
        background: #fff;
        border: 1px solid #e9ecef;
        border-radius: 12px;
        padding: 14px 18px;
        min-width: 170px;
        max-width: 300px;
        display: flex;
        flex-direction: column;
        gap: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

        .stat-label {
          font-size: 11px;
          color: #6c757d;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 500;
        }

        .stat-value {
          font-size: 22px;
          font-weight: 700;
          color: #212529;
        }
      }
    }
    .filter-container {
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .search-filters {
      display: flex;
      align-items: end;
      margin-bottom: 12px;
      gap: 8px;

      .search-btn {
        background-color: @main-green;
        border: none;
        border-radius: 100%;
        height: 34px;
        width: 34px;
        display: flex;
        cursor: pointer;
        align-items: center;
        justify-content: center;
        transition: opacity 0.1s ease-in-out;

        &:hover {
          opacity: 0.8;
        }
      }

      label {
        display: block;
        font-size: 15px;

        select {
          display: block;
          font-size: 15px;
          border-radius: 2px;
          margin-top: 5px;
          padding: 2px 10px;
          width: 160px;
          height: 35px;
          border: 1px solid #ddd;
        }

        input {
          display: block;
          font-size: 15px;
          border-radius: 2px;
          margin-top: 5px;
          padding: 2px 10px;
          width: 160px;
          height: 35px;
          border: 1px solid #ddd;
        }
      }
    }
  }
}
</style>
